import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, ChevronRight, RefreshCw } from "lucide-react";
import { api, fmtTime } from "@/lib/api";
import { SectionTitle, Badge } from "@/components/Bits";

const STATE_LABELS = {
  draft: { label: "Draft", color: "text-zinc-400" },
  paper: { label: "Paper", color: "text-blue-400" },
  live_subset: { label: "Live (subset)", color: "text-amber-400" },
  live: { label: "Live", color: "text-emerald-400" },
  paused: { label: "Paused", color: "text-orange-400" },
  retired: { label: "Retired", color: "text-zinc-600" },
};

const ALLOWED_TRANSITIONS = {
  // Phase 6 (2026-06-03): paper is OPTIONAL. draft → live_subset and
  // draft → live are first-class paths when the LIVE safety gates
  // are satisfied (paper_only_safety=false AND sizing usable).
  draft: ["paper", "live_subset", "live", "retired"],
  paper: ["live_subset", "live", "paused", "retired", "draft"],
  live_subset: ["live", "paused", "retired"],
  live: ["live_subset", "paused", "retired"],
  paused: ["paper", "live_subset", "live", "retired"],
  retired: [],
};

function StateChip({ state }) {
  const meta = STATE_LABELS[state] || { label: state, color: "text-zinc-400" };
  return (
    <span className={`text-xs font-mono ${meta.color}`} data-testid={`slot-state-${state}`}>
      {meta.label}
    </span>
  );
}

// ===================================================================
// EngineHealthPanel — Multi-Strategy Engine Health.
//
// Operator-facing "is each strategy actually running?" surface.
// One row per non-retired slot with a derived status badge:
//
//   RUNNING  (green)  — live + heartbeat < 5min + no error
//   STALE    (red)    — live + heartbeat > 5min (or missing)
//   ERROR    (red)    — live + last_error present
//   DISABLED (zinc)   — slot.state != live
//
// Backend source: GET /api/strategy-slots/_meta/health
// Polls every 15s. No analytics, no charts — pure observability.
// ===================================================================
const HEALTH_BADGE = {
  RUNNING:  { bg: "bg-emerald-900/40", border: "border-emerald-600/60", text: "text-emerald-300" },
  STALE:    { bg: "bg-red-900/40",     border: "border-red-600/60",     text: "text-red-300" },
  ERROR:    { bg: "bg-red-900/50",     border: "border-red-500",        text: "text-red-200" },
  DISABLED: { bg: "bg-zinc-900",       border: "border-zinc-700",       text: "text-zinc-400" },
};

function HealthBadge({ status }) {
  const c = HEALTH_BADGE[status] || HEALTH_BADGE.DISABLED;
  return (
    <span
      data-testid={`health-badge-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-wider ${c.bg} ${c.border} ${c.text}`}
    >
      {status}
    </span>
  );
}

function EngineHealthPanel() {
  const [rows, setRows] = useState([]);
  const [loadedAt, setLoadedAt] = useState(null);
  const [err, setErr] = useState(null);

  const reload = async () => {
    try {
      const h = await api.slotsHealth();
      setRows(Array.isArray(h) ? h : []);
      setLoadedAt(new Date());
      setErr(null);
    } catch (e) {
      setErr(e?.message || "fetch failed");
      // eslint-disable-next-line no-console
      console.warn("[engine-health] fetch failed", e?.message);
    }
  };

  useEffect(() => {
    reload();
    const id = setInterval(reload, 15_000);
    return () => clearInterval(id);
  }, []);

  const counts = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  const shortId = (id) => (id ? `${String(id).slice(0, 8)}…` : "—");

  return (
    <div
      className="border border-zinc-800 rounded-md overflow-x-auto"
      data-testid="engine-health-panel"
    >
      <div className="px-3 py-2 bg-zinc-900 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">
            Multi-Strategy Engine Health
          </div>
          <div className="flex items-center gap-2 text-[10px] font-mono">
            {["RUNNING", "STALE", "ERROR", "DISABLED"].map((s) => (
              <span key={s} data-testid={`health-count-${s}`}>
                <HealthBadge status={s} />{" "}
                <span className="text-zinc-500">{counts[s] || 0}</span>
              </span>
            ))}
          </div>
        </div>
        <div className="text-[11px] text-zinc-500 font-mono">
          {err
            ? <span className="text-red-400">err: {err}</span>
            : loadedAt
              ? `Updated ${_relTime(loadedAt.toISOString())}`
              : "Loading…"}
        </div>
      </div>
      <table className="w-full text-left text-[12px]" data-testid="engine-health-table">
        <thead>
          <tr className="bg-zinc-900/60 text-[10px] uppercase text-zinc-500">
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Strategy</th>
            <th className="px-3 py-2">Slot</th>
            <th className="px-3 py-2">Symbol</th>
            <th className="px-3 py-2">State</th>
            <th className="px-3 py-2">Sizing</th>
            <th className="px-3 py-2 text-right">fixed_usd</th>
            <th className="px-3 py-2">Last Checked</th>
            <th className="px-3 py-2">Last Result</th>
            <th className="px-3 py-2">Last Signal</th>
            <th className="px-3 py-2">Error</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={11}
                className="px-3 py-4 text-center text-zinc-500"
                data-testid="engine-health-empty"
              >
                No active slots. Create one below and enable the
                feature flag.
              </td>
            </tr>
          ) : (
            rows.map((r) => {
              const isCritical = r.status === "STALE" || r.status === "ERROR";
              const rowClass = isCritical
                ? "border-t border-red-900/40 bg-red-950/20 hover:bg-red-950/30"
                : "border-t border-zinc-800/40 hover:bg-zinc-900/40";
              const ageText =
                r.last_checked_age_seconds == null
                  ? "never"
                  : r.last_checked_age_seconds < 60
                    ? `${r.last_checked_age_seconds}s ago`
                    : r.last_checked_age_seconds < 3600
                      ? `${Math.floor(r.last_checked_age_seconds / 60)}m ago`
                      : `${Math.floor(r.last_checked_age_seconds / 3600)}h ago`;
              return (
                <tr
                  key={r.slot_id}
                  className={rowClass}
                  data-testid={`health-row-${r.slot_id}`}
                >
                  <td className="px-3 py-2">
                    <HealthBadge status={r.status} />
                  </td>
                  <td className="px-3 py-2 font-mono text-zinc-200">
                    {r.strategy_id || "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-zinc-500">
                    {shortId(r.slot_id)}
                  </td>
                  <td className="px-3 py-2 font-mono">{r.symbol || "—"}</td>
                  <td className="px-3 py-2"><StateChip state={r.state} /></td>
                  <td className="px-3 py-2 font-mono text-zinc-400">
                    {r.sizing_mode || "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {r.fixed_usd_amount ? `$${r.fixed_usd_amount}` : "—"}
                  </td>
                  <td
                    className={`px-3 py-2 font-mono ${isCritical ? "text-red-400" : "text-emerald-400"}`}
                    data-testid={`health-last-checked-${r.slot_id}`}
                  >
                    {ageText}
                  </td>
                  <td className="px-3 py-2 font-mono text-zinc-400">
                    {r.last_result || "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-zinc-500">
                    {_relTime(r.last_signal_at)}
                    {r.last_signal_side ? (
                      <span className="ml-1 text-[10px] uppercase text-zinc-600">
                        {r.last_signal_side}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-red-400 max-w-xs truncate">
                    {r.last_error || ""}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ===================================================================
// SlotTelemetryPanel — minimal "is each slot alive?" surface.
//
// One row per slot showing: strategy/symbol/state/sizing,
// last_seen_at (relative), last_signal_at, last_signal_side,
// last_result classification, and last_error if any.
//
// Polls /api/strategy-slots/_meta/telemetry every 15s. No charts,
// no analytics — just enough to confirm each active slot is being
// evaluated by the live engine.
// ===================================================================
const RESULT_COLORS = {
  no_signal:        "text-zinc-400",
  evaluated:        "text-zinc-400",
  signal_blocked:   "text-amber-400",
  signal_opened:    "text-emerald-400",
  exit_checked:     "text-blue-400",
  exit_triggered:   "text-emerald-300",
  error:            "text-red-400",
};

function _relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const dt = Math.floor((Date.now() - t) / 1000);
  if (dt < 60) return `${dt}s ago`;
  if (dt < 3600) return `${Math.floor(dt / 60)}m ago`;
  if (dt < 86400) return `${Math.floor(dt / 3600)}h ago`;
  return `${Math.floor(dt / 86400)}d ago`;
}

function SlotTelemetryPanel({ slots }) {
  const [rows, setRows] = useState([]);
  const [loadedAt, setLoadedAt] = useState(null);

  const reload = async () => {
    try {
      const t = await api.slotsTelemetry();
      setRows(Array.isArray(t) ? t : []);
      setLoadedAt(new Date());
    } catch (e) {
      // Best-effort UI surface — don't toast on every failure.
      // eslint-disable-next-line no-console
      console.warn("[telemetry] fetch failed", e?.message);
    }
  };

  useEffect(() => {
    reload();
    const id = setInterval(reload, 15_000);
    return () => clearInterval(id);
  }, []);

  // Join telemetry with slot rows by slot_id so we can show inactive
  // slots too (they won't have a telemetry doc until the engine
  // evaluates them at least once).
  const byId = Object.fromEntries(rows.map((r) => [r.slot_id, r]));
  const display = slots
    .filter((s) => (s.state || "") !== "retired")
    .map((s) => ({ slot: s, tel: byId[s.id] }));

  return (
    <div
      className="border border-zinc-800 rounded-md overflow-x-auto"
      data-testid="slot-telemetry-panel"
    >
      <div className="px-3 py-2 bg-zinc-900 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500">
          Slot Telemetry — Liveness Check
        </div>
        <div className="text-[11px] text-zinc-500 font-mono">
          {loadedAt ? `Updated ${_relTime(loadedAt.toISOString())}` : "Loading…"}
        </div>
      </div>
      <table className="w-full text-left text-[12px]" data-testid="slot-telemetry-table">
        <thead>
          <tr className="bg-zinc-900/60 text-[10px] uppercase text-zinc-500">
            <th className="px-3 py-2">Strategy</th>
            <th className="px-3 py-2">Symbol</th>
            <th className="px-3 py-2">State</th>
            <th className="px-3 py-2 text-right">Sizing</th>
            <th className="px-3 py-2 text-right">fixed_usd</th>
            <th className="px-3 py-2">Last Seen</th>
            <th className="px-3 py-2">Last Signal</th>
            <th className="px-3 py-2">Result</th>
            <th className="px-3 py-2">Error</th>
          </tr>
        </thead>
        <tbody>
          {display.length === 0 ? (
            <tr>
              <td colSpan={9} className="px-3 py-4 text-center text-zinc-500">
                No active slots. Create one below and enable the
                feature flag.
              </td>
            </tr>
          ) : (
            display.map(({ slot, tel }) => {
              const stale = tel?.last_seen_at
                ? (Date.now() - new Date(tel.last_seen_at).getTime()) > 5 * 60 * 1000
                : true;
              const resultColor = RESULT_COLORS[tel?.last_result] || "text-zinc-500";
              return (
                <tr
                  key={slot.id}
                  className="border-t border-zinc-800/40 hover:bg-zinc-900/40"
                  data-testid={`telemetry-row-${slot.id}`}
                >
                  <td className="px-3 py-2 font-mono">{slot.strategy_id}</td>
                  <td className="px-3 py-2 font-mono">{slot.symbol}</td>
                  <td className="px-3 py-2"><StateChip state={slot.state} /></td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-400">
                    {slot.sizing_mode}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {slot.fixed_usd_amount ? `$${slot.fixed_usd_amount}` : "—"}
                  </td>
                  <td className={`px-3 py-2 font-mono ${stale ? "text-red-400" : "text-emerald-400"}`}>
                    {_relTime(tel?.last_seen_at)}
                  </td>
                  <td className="px-3 py-2 font-mono text-zinc-400">
                    {_relTime(tel?.last_signal_at)}
                    {tel?.last_signal_side ? (
                      <span className="ml-1 text-[10px] uppercase text-zinc-500">
                        {tel.last_signal_side}
                      </span>
                    ) : null}
                  </td>
                  <td className={`px-3 py-2 font-mono ${resultColor}`}>
                    {tel?.last_result || "—"}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-red-400 max-w-xs truncate">
                    {tel?.last_error || ""}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ===================================================================
// FeatureFlagToggle — operator-controlled multi_strategy_enabled.
//
// Off  → legacy V2 singleton path. Slots are inert.
// On   → orchestrator dispatches per-slot intents. Slots in PAPER /
//        live_subset / live are consumed.
//
// Strong confirmation modal before flipping ON. Always shows the
// preflight summary (active slots, Σ allocation, paper_only_safety
// counts, live-state counts) so the operator confirms with full
// context. Backend audit-logs every flip.
// ===================================================================
function FeatureFlagToggle({ featureFlag, slots, onChanged }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [intent, setIntent] = useState(null);     // true = enable, false = disable
  const [busy, setBusy] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [loadingPre, setLoadingPre] = useState(false);

  if (!featureFlag) return null;
  const enabled = !!featureFlag.multi_strategy_enabled;

  const openConfirm = async (toEnable) => {
    setIntent(toEnable);
    setConfirmOpen(true);
    setLoadingPre(true);
    try {
      const pre = await api.slotsPreflight();
      setPreflight(pre);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "preflight failed");
    } finally {
      setLoadingPre(false);
    }
  };

  const cancel = () => {
    setConfirmOpen(false);
    setIntent(null);
    setPreflight(null);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await api.slotsSetFeatureFlag(intent);
      toast.success(
        `multi_strategy_enabled = ${String(res.current)} (was ${String(res.previous)})`
      );
      setConfirmOpen(false);
      setIntent(null);
      setPreflight(null);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "flag flip failed");
    } finally {
      setBusy(false);
    }
  };

  // Compute risk indicators on the live cache; preflight overrides
  // these once fetched in the modal.
  const active = slots.filter((s) => s.state !== "draft" && s.state !== "retired");
  const totalAlloc = slots
    .filter((s) => s.state !== "retired")
    .reduce((a, s) => a + Number(s.allocation_pct || 0), 0);
  const liveCount = active.filter((s) => s.state === "live" || s.state === "live_subset").length;
  const paperOnlyCount = active.filter((s) => !!s.paper_only_safety).length;

  return (
    <>
      <div
        data-testid="slots-feature-flag-row"
        className={`rounded border px-4 py-3 flex items-center justify-between ${
          enabled
            ? "border-emerald-700 bg-emerald-950/40"
            : "border-amber-700 bg-amber-950/40"
        }`}
      >
        <div className="flex flex-col gap-1">
          <div className="text-sm font-semibold">
            <span
              data-testid="slots-feature-flag-state"
              className={enabled ? "text-emerald-300" : "text-amber-300"}
            >
              multi_strategy_enabled = {String(enabled)}
            </span>
            <span className="ml-3 text-xs text-zinc-500 font-mono">
              {enabled ? "orchestrator is dispatching slots" : "legacy V2 singleton path"}
            </span>
          </div>
          <div className="text-[11px] text-zinc-400 font-mono">
            active slots: {active.length} · Σ alloc: {totalAlloc.toFixed(1)}% ·
            {" "}paper_only_safety on: {paperOnlyCount} · live/live_subset: {liveCount}
          </div>
        </div>
        <Button
          data-testid="slots-feature-flag-toggle"
          onClick={() => openConfirm(!enabled)}
          className={
            enabled
              ? "bg-red-700 hover:bg-red-600 text-white"
              : "bg-emerald-700 hover:bg-emerald-600 text-white"
          }
        >
          {enabled ? "Disable multi-strategy" : "Enable multi-strategy"}
        </Button>
      </div>

      {confirmOpen && (
        <div
          data-testid="slots-feature-flag-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={cancel}
        >
          <div
            className="bg-zinc-950 border border-zinc-700 rounded-lg max-w-xl w-full mx-4 p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-base font-semibold text-zinc-100">
              {intent ? "Enable multi-strategy execution?" : "Disable multi-strategy execution?"}
            </div>
            <div
              data-testid="slots-feature-flag-modal-text"
              className="text-xs text-amber-300 leading-relaxed border border-amber-900/50 bg-amber-950/30 rounded px-3 py-2"
            >
              {intent
                ? "Enabling multi_strategy_enabled will activate Strategy Slots execution. Confirm only after slots are configured correctly."
                : "Disabling multi_strategy_enabled returns the engine to the legacy V2 singleton path. Existing positions on the exchange are NOT closed."}
            </div>

            <div className="text-xs text-zinc-300">
              <div className="font-mono mb-1 text-zinc-400">Preflight snapshot</div>
              {loadingPre && <div className="text-zinc-500 italic">Loading…</div>}
              {!loadingPre && preflight && (
                <div className="font-mono space-y-1">
                  <div>active_slots: <span className="text-zinc-100">{preflight.active_slot_count}</span></div>
                  <div>total_allocation_pct: <span className="text-zinc-100">{preflight.total_allocation_pct}</span></div>
                  <div>paper_only_safety on: <span className="text-zinc-100">{preflight.paper_only_safety_count}</span></div>
                  <div>live / live_subset count: <span className="text-zinc-100">{preflight.live_or_live_subset_count}</span></div>
                  {Array.isArray(preflight.warnings) && preflight.warnings.length > 0 && (
                    <div className="mt-2 text-amber-400">
                      <div className="font-bold">Warnings:</div>
                      <ul className="list-disc list-inside">
                        {preflight.warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(preflight.active_slots) && preflight.active_slots.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-zinc-800">
                      <div className="text-zinc-400 mb-1">Active slot detail:</div>
                      <table className="w-full text-[11px]">
                        <thead className="text-zinc-500">
                          <tr><th className="text-left">id</th><th className="text-left">name</th><th className="text-left">state</th><th className="text-right">alloc</th><th className="text-center">paper_only</th></tr>
                        </thead>
                        <tbody>
                          {preflight.active_slots.map((s) => (
                            <tr key={s.id} className="text-zinc-300">
                              <td>{s.id.slice(0,8)}…</td>
                              <td>{s.name}</td>
                              <td>{s.state}</td>
                              <td className="text-right">{Number(s.allocation_pct).toFixed(1)}%</td>
                              <td className="text-center">{s.paper_only_safety ? "ON" : "off"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                data-testid="slots-feature-flag-modal-cancel"
                variant="outline"
                onClick={cancel}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                data-testid="slots-feature-flag-modal-confirm"
                onClick={confirm}
                disabled={busy || loadingPre}
                className={
                  intent
                    ? "bg-emerald-700 hover:bg-emerald-600 text-white"
                    : "bg-red-700 hover:bg-red-600 text-white"
                }
              >
                {busy ? "Applying…" : intent ? "Confirm enable" : "Confirm disable"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ===================================================================
// TemplateSlotForm — production activation surface.
// Operator picks ONE template; slot is created in DRAFT with all the
// validated parameters pre-loaded. Zero knobs on this path.
// Research / parameter editing lives elsewhere (Preview only).
// ===================================================================
function TemplateSlotForm({ onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = await api.strategyTemplatesList();
        if (cancelled) return;
        const list = Array.isArray(out) ? out : [];
        setTemplates(list);
        if (list.length > 0) setSelectedId(list[0].id);
      } catch (e) {
        if (cancelled) return;
        setLoadErr(e?.response?.data?.detail || e?.message || "load failed");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selected = templates.find((t) => t.id === selectedId);

  const submit = async () => {
    if (!selectedId) {
      toast.error("Select a template first");
      return;
    }
    setBusy(true);
    try {
      const slot = await api.slotsFromTemplate(selectedId);
      toast.success(`Slot "${slot.name}" created (state=${slot.state})`);
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "create from template failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="slot-from-template-card"
      className="border border-emerald-900/40 bg-emerald-950/10 rounded-md p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-emerald-300">
          Create from Validated Template
        </div>
        <div className="text-[11px] text-zinc-500 font-mono">
          {templates.length} template{templates.length === 1 ? "" : "s"} available
        </div>
      </div>
      {loadErr && (
        <div className="text-xs text-red-400">Failed to load templates: {loadErr}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <label className="text-xs text-zinc-400 flex flex-col gap-1 md:col-span-2">
          Template
          <select
            data-testid="slot-template-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={templates.length === 0}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100"
          >
            {templates.length === 0 && <option value="">No templates available</option>}
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </label>
        <Button
          data-testid="slot-template-submit"
          onClick={submit}
          disabled={busy || !selectedId}
          className="bg-emerald-700 hover:bg-emerald-600 text-white"
        >
          <Plus size={14} className="mr-1" />
          {busy ? "Creating…" : "Create slot"}
        </Button>
      </div>
      {selected && (
        <div
          data-testid="slot-template-summary"
          className="text-xs text-zinc-400 font-mono leading-relaxed border-t border-zinc-800 pt-2"
        >
          <div>
            <span className="text-zinc-500">strategy_id:</span> {selected.strategy_id}
            <span className="ml-3 text-zinc-500">symbol:</span> {selected.symbol}
            <span className="ml-3 text-zinc-500">tf:</span> {selected.timeframe}
            <span className="ml-3 text-zinc-500">exchange:</span> {selected.exchange}
          </div>
          <div className="text-zinc-500 mt-1 text-[11px]">{selected.description}</div>
          <div className="text-amber-400 mt-1 text-[11px]">
            Slot will be created in DRAFT with allocation=0% and paper_only_safety=ON.
            Promote state explicitly before any live trading.
          </div>
        </div>
      )}
    </div>
  );
}

function CreateSlotForm({ profiles, onCreated }) {
  const [form, setForm] = useState({
    strategy_id: "MDIV",
    strategy_profile_id: "",
    name: "",
    allocation_pct: 0,
    max_open_long: 1,
    max_open_short: 1,
    max_open_total: 2,
    // Phase 5: production sizing default is `fixed_usd` — operator
    // enters an absolute USDT order size per slot. `allocation_pct`
    // mode remains available via the dropdown.
    sizing_mode: "fixed_usd",
    sizing_value: 100,
    fixed_usd_amount: 250,
    leverage: 25,
    symbol: "BTCUSDT",
    timeframe: "5m",
    exchange: "lbank",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setNum = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value === "" ? "" : Number(e.target.value) }));
  const isFixedUsd = form.sizing_mode === "fixed_usd"
    || form.sizing_mode === "fixed_usdt";

  const submit = async () => {
    if (!form.strategy_profile_id) {
      toast.error("Select a strategy profile");
      return;
    }
    if (!form.name || !form.strategy_id) {
      toast.error("Name and strategy_id are required");
      return;
    }
    if (form.sizing_mode === "fixed_usd"
        && (!form.fixed_usd_amount || Number(form.fixed_usd_amount) <= 0)) {
      toast.error("Order Size USDT must be > 0 when Sizing Mode = Fixed USD");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        allocation_pct: Number(form.allocation_pct || 0),
        max_open_long: Number(form.max_open_long),
        max_open_short: Number(form.max_open_short),
        max_open_total: Number(form.max_open_total),
        sizing_value: Number(form.sizing_value),
        leverage: Number(form.leverage),
      };
      // fixed_usd_amount is only meaningful in fixed_usd mode; for
      // allocation_pct slots we omit it so the backend stores null.
      if (form.sizing_mode === "fixed_usd") {
        payload.fixed_usd_amount = Number(form.fixed_usd_amount);
      } else {
        delete payload.fixed_usd_amount;
      }
      const created = await api.slotsCreate(payload);
      toast.success(`Slot "${created.name}" created (state=draft)`);
      onCreated();
      setForm((p) => ({ ...p, name: "" }));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "create failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-zinc-800 rounded-md p-4 space-y-3 bg-zinc-950">
      <div className="text-sm font-semibold text-zinc-300">Create Strategy Slot</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          strategy_id (logical name, e.g. MDIV)
          <input
            data-testid="slot-form-strategy-id"
            value={form.strategy_id}
            onChange={set("strategy_id")}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100"
          />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1 md:col-span-2">
          strategy_profile_id
          <select
            data-testid="slot-form-profile-id"
            value={form.strategy_profile_id}
            onChange={set("strategy_profile_id")}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100"
          >
            <option value="">-- select profile --</option>
            {profiles.map((p) => (
              <option key={p.id || p.name} value={p.id || ""}>
                {p.name} {p.id ? `(${String(p.id).slice(0, 8)}…)` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          name (display label)
          <input
            data-testid="slot-form-name"
            value={form.name}
            onChange={set("name")}
            placeholder="e.g. MDIV 5m thr=2 TP=1.0R"
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100"
          />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          symbol
          <input data-testid="slot-form-symbol" value={form.symbol} onChange={set("symbol")}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          timeframe
          <input data-testid="slot-form-timeframe" value={form.timeframe} onChange={set("timeframe")}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          allocation_pct (0–100)
          <input data-testid="slot-form-allocation"
            type="number" min={0} max={100} step={0.1}
            value={form.allocation_pct} onChange={setNum("allocation_pct")}
            disabled={isFixedUsd}
            className={`bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100 ${isFixedUsd ? "opacity-40" : ""}`} />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          max_open_long
          <input data-testid="slot-form-max-long" type="number" min={0} max={10}
            value={form.max_open_long} onChange={setNum("max_open_long")}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          max_open_short
          <input data-testid="slot-form-max-short" type="number" min={0} max={10}
            value={form.max_open_short} onChange={setNum("max_open_short")}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          max_open_total
          <input data-testid="slot-form-max-total" type="number" min={0} max={20}
            value={form.max_open_total} onChange={setNum("max_open_total")}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Sizing Mode
          <select
            data-testid="slot-form-sizing-mode"
            value={form.sizing_mode}
            onChange={set("sizing_mode")}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100"
          >
            <option value="fixed_usd">Fixed USD (per-order USDT amount)</option>
            <option value="allocation_pct">Allocation % (of equity)</option>
            <option value="equity_pct">equity_pct (legacy alias)</option>
            <option value="fixed_usdt">fixed_usdt (legacy alias)</option>
          </select>
        </label>
        {isFixedUsd ? (
          <label className="text-xs text-zinc-400 flex flex-col gap-1">
            Order Size USDT
            <input data-testid="slot-form-fixed-usd-amount" type="number" min={1} step={1}
              value={form.fixed_usd_amount} onChange={setNum("fixed_usd_amount")}
              className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100" />
          </label>
        ) : (
          <label className="text-xs text-zinc-400 flex flex-col gap-1">
            sizing_value (intensity %)
            <input data-testid="slot-form-sizing-value" type="number" min={1}
              value={form.sizing_value} onChange={setNum("sizing_value")}
              className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100" />
          </label>
        )}
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          leverage
          <input data-testid="slot-form-leverage" type="number" min={1} max={125}
            value={form.leverage} onChange={setNum("leverage")}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100" />
        </label>
      </div>
      <Button
        data-testid="slot-form-submit"
        disabled={submitting}
        onClick={submit}
        className="bg-emerald-700 hover:bg-emerald-600 text-white"
      >
        <Plus size={14} className="mr-1" />
        {submitting ? "Creating…" : "Create slot"}
      </Button>
    </div>
  );
}

function SlotRow({ slot, onTransition, onDelete, onPatched }) {
  const allowed = ALLOWED_TRANSITIONS[slot.state] || [];
  const isFixedUsd = (slot.sizing_mode === "fixed_usd"
                        || slot.sizing_mode === "fixed_usdt");
  // Per-slot inline editor for fixed_usd_amount — the operator
  // configures BTC/ETH/BNB MDIV amounts here before activating.
  const [editingAmount, setEditingAmount] = useState(false);
  const [draftAmount, setDraftAmount] = useState(
    slot.fixed_usd_amount ?? slot.sizing_value ?? 0);
  const canEditAmount = slot.state === "draft" || slot.state === "paper";
  const saveAmount = async () => {
    const n = Number(draftAmount);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Order Size USDT must be > 0");
      return;
    }
    try {
      await api.slotsUpdate(slot.id, { fixed_usd_amount: n });
      toast.success(`${slot.name}: order size → ${n} USDT`);
      setEditingAmount(false);
      onPatched && onPatched();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "patch failed");
    }
  };
  return (
    <tr
      data-testid={`slot-row-${slot.id}`}
      className="border-b border-zinc-800 hover:bg-zinc-900/50"
    >
      <td className="px-3 py-2 text-xs font-mono text-zinc-500">
        {slot.id ? `${String(slot.id).slice(0, 8)}…` : "—"}
      </td>
      <td className="px-3 py-2 text-sm text-zinc-100">{slot.name}</td>
      <td className="px-3 py-2 text-xs text-zinc-400">
        <Badge>{slot.strategy_id}</Badge>
      </td>
      <td className="px-3 py-2 text-xs font-mono text-zinc-500">
        {slot.strategy_profile_id ? `${String(slot.strategy_profile_id).slice(0, 8)}…` : "—"}
      </td>
      <td className="px-3 py-2 text-xs text-zinc-300">
        {slot.symbol} {slot.timeframe}
      </td>
      <td className="px-3 py-2 text-xs text-zinc-300 text-right">
        {isFixedUsd
          ? <span className="text-zinc-600">—</span>
          : `${Number(slot.allocation_pct || 0).toFixed(1)}%`}
      </td>
      <td className="px-3 py-2 text-xs text-zinc-300 text-right">
        L{slot.max_open_long}/S{slot.max_open_short}/T{slot.max_open_total}
      </td>
      <td
        className="px-3 py-2 text-xs text-zinc-300 text-right"
        data-testid={`slot-sizing-cell-${slot.id}`}
      >
        {isFixedUsd ? (
          editingAmount ? (
            <span className="inline-flex items-center gap-1">
              <input
                data-testid={`slot-fixed-usd-input-${slot.id}`}
                type="number" min={1} step={1}
                value={draftAmount}
                onChange={(e) => setDraftAmount(e.target.value)}
                className="w-20 bg-zinc-900 border border-zinc-700 rounded px-1 py-0.5 text-xs text-right text-zinc-100"
              />
              <button
                data-testid={`slot-fixed-usd-save-${slot.id}`}
                onClick={saveAmount}
                className="text-[10px] px-1 py-0.5 rounded bg-emerald-700 hover:bg-emerald-600 text-white"
              >save</button>
              <button
                onClick={() => { setEditingAmount(false);
                  setDraftAmount(slot.fixed_usd_amount ?? slot.sizing_value ?? 0); }}
                className="text-[10px] px-1 py-0.5 rounded border border-zinc-700 text-zinc-300"
              >x</button>
            </span>
          ) : (
            <button
              data-testid={`slot-fixed-usd-edit-${slot.id}`}
              onClick={() => canEditAmount && setEditingAmount(true)}
              disabled={!canEditAmount}
              className={`font-mono ${canEditAmount ? "underline decoration-dotted text-emerald-300 hover:text-emerald-200" : "text-zinc-400 cursor-not-allowed"}`}
              title={canEditAmount
                ? "Click to edit Order Size USDT"
                : "Order size locked once promoted past `paper`"}
            >
              ${Number(slot.fixed_usd_amount ?? slot.sizing_value ?? 0).toFixed(0)} USDT @ {slot.leverage}x
            </button>
          )
        ) : (
          <span>
            {Number(slot.sizing_value || 0).toFixed(0)}% @ {slot.leverage}x
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <StateChip state={slot.state} />
      </td>
      <td className="px-3 py-2 text-xs text-zinc-500">{fmtTime(slot.created_at)}</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1 flex-wrap">
          {/* Enable Live Trading — surgical button that ONLY flips
              paper_only_safety to false. Required before promoting
              past `paper` (the server rejects the transition with
              `live_blocked_paper_only_safety` otherwise). */}
          {slot.paper_only_safety && (
            <Button
              data-testid={`slot-enable-live-${slot.id}`}
              size="sm"
              variant="outline"
              className="h-6 px-2 text-[10px] uppercase border-amber-700 text-amber-300 hover:bg-amber-950"
              onClick={async () => {
                if (!window.confirm(
                    `Enable LIVE trading for ${slot.name}?\n\n` +
                    "This ONLY clears the paper_only_safety flag. " +
                    "It does NOT promote the slot to live. " +
                    "You still need to transition the state " +
                    "(draft → paper → live_subset → live).")) {
                  return;
                }
                try {
                  await api.slotsUpdate(slot.id,
                    { paper_only_safety: false });
                  toast.success(
                    `${slot.name}: paper_only_safety cleared`);
                  onPatched && onPatched();
                } catch (e) {
                  toast.error(e?.response?.data?.detail
                    || "patch failed");
                }
              }}
              title="Clears paper_only_safety so the slot can be promoted past `paper`"
            >
              Enable Live Trading
            </Button>
          )}
          {allowed.map((tgt) => (
            <Button
              key={tgt}
              data-testid={`slot-transition-${slot.id}-${tgt}`}
              size="sm"
              variant="outline"
              className="h-6 px-2 text-[10px] uppercase border-zinc-700 text-zinc-200"
              onClick={() => onTransition(slot, tgt)}
            >
              <ChevronRight size={10} className="mr-0.5" />
              {tgt}
            </Button>
          ))}
          {(slot.state === "draft" || slot.state === "retired") && (
            <Button
              data-testid={`slot-delete-${slot.id}`}
              size="sm"
              variant="outline"
              className="h-6 px-2 text-[10px] uppercase border-red-900 text-red-300"
              onClick={() => onDelete(slot)}
            >
              <Trash2 size={10} />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function StrategySlots() {
  const [slots, setSlots] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [featureFlag, setFeatureFlag] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, p, ff] = await Promise.all([
        api.slotsList(),
        api.strategyProfiles(),
        api.slotsFeatureFlag(),
      ]);
      setSlots(Array.isArray(s) ? s : []);
      setProfiles(Array.isArray(p) ? p : []);
      setFeatureFlag(ff);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const transition = async (slot, target) => {
    try {
      await api.slotsTransition(slot.id, target);
      toast.success(`Slot "${slot.name}" → ${target}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "transition failed");
    }
  };

  const remove = async (slot) => {
    if (!window.confirm(`Delete slot "${slot.name}"? This is irreversible.`)) return;
    try {
      await api.slotsDelete(slot.id);
      toast.success("Slot deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "delete failed");
    }
  };

  const totalAllocation = slots
    .filter((s) => s.state !== "retired")
    .reduce((acc, s) => acc + Number(s.allocation_pct || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="strategy-slots-page">
      <SectionTitle>Strategy Slots — Multi-Strategy Architecture</SectionTitle>

      <EngineHealthPanel />

      <FeatureFlagToggle
        featureFlag={featureFlag}
        slots={slots}
        onChanged={reload}
      />

      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400">
          {slots.length} slot{slots.length !== 1 ? "s" : ""} ·{" "}
          <span className="font-mono">
            Σ allocation (non-retired): {totalAllocation.toFixed(1)}%
          </span>
        </div>
        <Button
          data-testid="slots-reload-btn"
          onClick={reload}
          disabled={loading}
          variant="outline"
          size="sm"
        >
          <RefreshCw size={12} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
          Reload
        </Button>
      </div>

      <TemplateSlotForm onCreated={reload} />

      <CreateSlotForm profiles={profiles} onCreated={reload} />

      <SlotTelemetryPanel slots={slots} />

      <div className="border border-zinc-800 rounded-md overflow-x-auto">
        <table className="w-full text-left" data-testid="slots-table">
          <thead>
            <tr className="bg-zinc-900 text-[11px] uppercase text-zinc-500">
              <th className="px-3 py-2">ID</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">strategy_id</th>
              <th className="px-3 py-2">profile_id</th>
              <th className="px-3 py-2">Market</th>
              <th className="px-3 py-2 text-right">Alloc%</th>
              <th className="px-3 py-2 text-right">Caps</th>
              <th className="px-3 py-2 text-right">Sizing</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {slots.length === 0 ? (
              <tr>
                <td
                  colSpan={11}
                  className="px-3 py-8 text-center text-sm text-zinc-500"
                  data-testid="slots-empty-state"
                >
                  No slots yet. Create one above. Production behavior is unchanged
                  until at least one slot is in <span className="font-mono">paper</span>{" "}
                  / <span className="font-mono">live_subset</span> /{" "}
                  <span className="font-mono">live</span> state AND the feature flag
                  is enabled.
                </td>
              </tr>
            ) : (
              slots.map((slot) => (
                <SlotRow
                  key={slot.id}
                  slot={slot}
                  onTransition={transition}
                  onDelete={remove}
                  onPatched={reload}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
